import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { placeTooltip, type TooltipPlacement, type TooltipSide } from "./tooltipPlacement";

interface Props {
  text: string;
  /** When true, the icon flows inline next to its sibling content (e.g. a table cell label)
   *  instead of being absolutely positioned in the parent's top-right corner. */
  inline?: boolean;
  /** Preferred side of the icon for the body. The body flips to the other side on its own when
   *  the preferred one has no room, so this is a hint, not a guarantee. */
  placement?: TooltipSide;
}

/**
 * Small info icon (Material Symbols Rounded `info`) that reveals a tooltip on hover or keyboard
 * focus. The body is rendered in a portal on document.body with fixed coordinates computed from
 * the icon's position, so it is never clipped by a card, a scrolling table or the viewport edge:
 * it is centred on the icon, clamped to the viewport, and flips above the icon when there is no
 * room below (see tooltipPlacement.ts). Stops click propagation so it works inside clickable tiles.
 */
export function InfoTooltip({ text, inline = false, placement = "bottom" }: Props) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bodyRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPlacement | null>(null);

  // Measure the body once it exists, then position it. Re-run while open if the preference changes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const anchor = triggerRef.current?.getBoundingClientRect();
    const body = bodyRef.current;
    if (!anchor || !body) return;
    setPos(
      placeTooltip(
        anchor,
        { width: body.offsetWidth, height: body.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        placement,
      ),
    );
  }, [open, placement, text]);

  // A fixed-position body would stay put while the page scrolls under it; close instead.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const className = "info-tooltip" + (inline ? " info-tooltip-inline" : "");
  const bodyClass = "info-tooltip-body" + (pos ? ` is-open is-${pos.side}` : "");

  return (
    <span
      ref={triggerRef}
      className={className}
      // tabIndex=0 makes the icon keyboard-focusable; focus opens the tooltip like hover does.
      tabIndex={0}
      aria-describedby={open ? id : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Don't let Space/Enter on the focused icon trigger an ancestor button.
        if (e.key === " " || e.key === "Enter") e.stopPropagation();
      }}
    >
      <span className="material-symbols-rounded info-tooltip-icon" aria-hidden="true">
        info
      </span>
      {open &&
        createPortal(
          <span
            ref={bodyRef}
            className={bodyClass}
            id={id}
            role="tooltip"
            style={
              pos
                ? ({ top: pos.top, left: pos.left, "--tt-arrow-x": `${pos.arrowLeft}px` } as CSSProperties)
                : undefined
            }
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
